import React from 'react'
import PropTypes from 'prop-types'
import ReactTooltip from 'react-tooltip'
import { Info, CloudX } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import DoughnutChart from '../../Charts/DoughnutChart/DoughnutChart'
import Icon from '../../Icons'
import { APP_COLORS } from '../../../helpers/constants'

import { formatPercentage, getPercentageColor, isAppDownloadsDataValid } from './utils'

const CHART_COLORS = [APP_COLORS.purple500, APP_COLORS.blue500]
const CHART_LABEL = 'New Users'

const AppData = ({ appDownloadsData, loading, datesDescriptions }) => {
  const { t } = useTranslation(['building-analytics'])
  const iosLabel = t('building-analytics:appData.iosLabel')
  const androidLabel = t('building-analytics:appData.androidLabel')
  const CHART_LABELS = [iosLabel, androidLabel]

  const getAppData = () => {
    return isAppDownloadsDataValid(appDownloadsData) ? (
      <React.Fragment>
        {appDownloadsData.appDownloads ? (
          <DoughnutChart
            halfChart
            disableLegend
            data={{
              labels: CHART_LABELS,
              datasets: [
                {
                  label: CHART_LABEL,
                  data: [
                    appDownloadsData.appDownloads.ios.total.value,
                    appDownloadsData.appDownloads.android.total.value,
                  ],
                  backgroundColor: CHART_COLORS,
                  borderColor: CHART_COLORS,
                  borderWidth: 1,
                  diff: [
                    appDownloadsData.appDownloads.ios.total.percentageDiff,
                    appDownloadsData.appDownloads.android.total.percentageDiff,
                  ],
                },
              ],
            }}
            options={{
              legend: {
                position: 'bottom',
                labels: {
                  boxWidth: 12,
                  fontFamily: 'Montserrat, Arial, sans-serif',
                  fontColor: APP_COLORS.primary,
                },
              },
              plugins: {
                doughnutlabel: {
                  labels: [
                    {
                      text: appDownloadsData.appDownloads.total.downloads.value,
                      font: {
                        size: '25',
                        family: 'Montserrat',
                      },
                      color: APP_COLORS.black300,
                    },
                    {
                      text: formatPercentage(
                        appDownloadsData.appDownloads.total.downloads.percentageDiff,
                      ),
                      font: {
                        size: '15',
                        weight: '500',
                        family: 'Montserrat',
                      },
                      color: getPercentageColor(
                        appDownloadsData.appDownloads.total.downloads.percentageDiff,
                      ),
                    },
                  ],
                },
                datalabels: {
                  display: false,
                },
              },
            }}
            tooltipCallbacks={{
              label: (tooltipItem, data) => {
                const platform = data.labels[tooltipItem.index]
                const value = data.datasets[0].data[tooltipItem.index]
                return `${platform}: ${value}`
              },
              footer: (tooltipItems, data) => {
                const tooltipItem = tooltipItems[0]
                const percentChange = data.datasets[0].diff[tooltipItem.index]
                return formatPercentage(percentChange)
              },
            }}
          />
        ) : (
          <div className="no-data">
            <CloudX size={24} />
            <h3>{t('building-analytics:common.noDataTitle')}</h3>
            <p>{t('building-analytics:common.noDataDescription')}</p>
          </div>
        )}
        <div className="subblock-wrapper">
          <h4 className="subblock-title">{iosLabel}</h4>
          <div className="subblock-line">
            <span className="subblock-text">{t('building-analytics:appData.appDownloads')}</span>{' '}
            <div className="subblock-info-icon" data-tip data-for="appDownloadsIos">
              <Info size={20} />
              <ReactTooltip multiline={true} id="appDownloadsIos" place="top" effect="solid">
                {t('building-analytics:appData.downloadsTooltip')}
              </ReactTooltip>
            </div>
            <span className="subblock-bold-text">
              {appDownloadsData.appDownloads.ios.total.value}
            </span>
            <span
              className="subblock-percent-text"
              style={{
                color: getPercentageColor(appDownloadsData.appDownloads.ios.total.percentageDiff),
              }}
            >
              {formatPercentage(appDownloadsData.appDownloads.ios.total.percentageDiff)}
            </span>
          </div>
        </div>
        <div className="subblock-wrapper">
          <h4 className="subblock-title">{androidLabel}</h4>
          <div className="subblock-line">
            <span className="subblock-text">{t('building-analytics:appData.appDownloads')}</span>{' '}
            <div className="subblock-info-icon" data-tip data-for="appDownloadsAndroid">
              <Info size={20} />
              <ReactTooltip multiline={true} id="appDownloadsAndroid" place="top" effect="solid">
                {t('building-analytics:appData.downloadsTooltip')}
              </ReactTooltip>
            </div>
            <span className="subblock-bold-text">
              {appDownloadsData.appDownloads.android.total.value}
            </span>
            <span
              className="subblock-percent-text"
              style={{
                color: getPercentageColor(
                  appDownloadsData.appDownloads.android.total.percentageDiff,
                ),
              }}
            >
              {formatPercentage(appDownloadsData.appDownloads.android.total.percentageDiff)}
            </span>
          </div>
        </div>
      </React.Fragment>
    ) : (
      <React.Fragment>
        <div className="no-data">
          <CloudX size={24} />
          <h3>{t('building-analytics:common.noDataTitle')}</h3>
          <p>{t('building-analytics:common.noDataDescription')}</p>
        </div>
      </React.Fragment>
    )
  }

  return (
    <div className="app-data">
      <div className="app-data__header">
        <div className="title-with-icon">
          <Icon styles={{ width: '22px', height: '22px' }} viewBox="0 0 24 24" icon="appData" />
          <h3>{t('building-analytics:appData.title')}</h3>
        </div>
        <p>
          {t('building-analytics:appData.dateRange', {
            value: datesDescriptions && datesDescriptions[0],
          })}
        </p>
        <p>
          {t('building-analytics:appData.comparedTo', {
            value: datesDescriptions && datesDescriptions[1],
          })}
        </p>
      </div>
      {loading ? (
        <div className="loading">
          <h3>....</h3>
          <h3>{t('building-analytics:common.loading')}</h3>
        </div>
      ) : (
        <div className="app-data__main-block">{getAppData()}</div>
      )}
    </div>
  )
}

AppData.propTypes = {
  appDownloadsData: PropTypes.object,
  loading: PropTypes.bool,
  datesDescriptions: PropTypes.array,
}
export default AppData
